import dayjs, { OptionType } from 'dayjs';

import {
  FOLLOWED_CURRENCY_RATE_TYPE,
  ICurrency,
  IRecipientMeta,
  Nullable,
} from 'types';
import { Masks } from 'utils';
import _get from 'lodash.get';
import { URLSearchParams } from 'url';
import { UseCurrencyRateReturnValues } from 'hooks/useCurrencyRate';
import {
  IEntityAccountDetailsType,
  IEntityPriorityAccountDetails,
  IEntityRegularAccountDetails,
  IEntityRegularSwiftAccountDetails,
} from 'types/entities';

export const asyncForEach = async (array: any[], callback: Function) => {
  for (let index = 0; index < array.length; index += 1) {
    await callback(array[index], index, array);
  }
};

export const parseIntoDateString = (value: Date) =>
  dayjs(value).format('MM DD YYYY');

export const parseIntoCurrencyString = (
  value: number | undefined,
  precision = 2
) => {
  if (typeof value === 'undefined' || typeof value !== 'number') {
    return '';
  }

  const valueWithPrecisionApplied = roundToPrecision(value, precision);

  return Masks.stringToCurrencyNumberString(
    valueWithPrecisionApplied.toString(),
    precision
  );
};

export const parseIntoCurrencyStringWithSymbol = (
  value: Nullable<number>,
  currencySymbol: Nullable<string> = '',
  precision = 0
) => {
  if (typeof value === 'undefined' || typeof value !== 'number') {
    return '';
  }

  const isNegative = value < 0;

  return `${isNegative ? '-' : ''}${currencySymbol}${parseIntoCurrencyString(
    value,
    precision
  )}`;
};

export const parseIntoShortNumberString = (
  value?: number,
  precision?: number
) => {
  if (typeof value === 'undefined' || typeof value !== 'number') {
    return '';
  }

  const lookup = [
    { value: 1e9, symbol: 'B', digits: 1 },
    { value: 1e6, symbol: 'M', digits: 1 },
    { value: 1e3, symbol: 'K', digits: 1 },
    { value: 1, symbol: '', digits: 0 },
  ];
  const lookupItem = lookup.find((item) => Math.abs(value) >= item.value);

  if (!lookupItem) {
    return '0';
  }

  const roundedValue = roundToPrecision(
    value / lookupItem.value,
    precision ?? lookupItem.digits
  );

  return roundedValue + lookupItem.symbol;
};

export const parseRate = (rate: Nullable<number>) =>
  rate ? roundToPrecision(rate, rate < 1 ? 4 : 5) : rate;

export const parseRateWithPrecision = (rate: Nullable<number>) =>
  rate ? rate.toPrecision(rate < 1 ? 4 : 5) : 'N/A';

export const getCountryCodeByCurrency = (
  currencyCode: Nullable<ICurrency['code']>,
  currencies: Nullable<ICurrency[]>
): ICurrency['countryCode'] => {
  if (!currencies || !currencyCode) {
    return '';
  }

  const currency = currencies.find((item) => item.code === currencyCode);

  if (currency) {
    return currency.countryCode;
  }

  return '';
};

export const roundToPrecision = (amount: number, precision = 2) => {
  if (!amount) return amount;

  const usePrecision = Math.pow(10, precision);

  const roundedAmount = Math.round(amount * usePrecision) / usePrecision;

  return roundedAmount;
};

export const replaceStringAt = (
  originalText: string,
  textForReplacement: string,
  index: number
) => {
  return (
    originalText.substr(0, index) +
    textForReplacement +
    originalText.substr(index + textForReplacement.length)
  );
};

export const camelCaseToSentenceCase = (
  camelCaseText: string,
  upperCaseFirstLetter: boolean = true
) => {
  const sentenceCaseText = camelCaseText.replace(/([A-Z])/g, ' $1');

  if (upperCaseFirstLetter) {
    return sentenceCaseText.charAt(0).toUpperCase() + sentenceCaseText.slice(1);
  }

  return sentenceCaseText;
};

export const canBeRegularCalc = (
  recipientCreationMetadata: Nullable<IRecipientMeta>
) => {
  if (!recipientCreationMetadata) {
    return false;
  }

  return (
    _get(recipientCreationMetadata, 'company.regular', null) ||
    _get(recipientCreationMetadata, 'individual.regular', null)
  );
};

export const canBeSwiftCalc = (
  recipientCreationMetadata: Nullable<IRecipientMeta>
) => {
  if (!recipientCreationMetadata) {
    return false;
  }

  return (
    _get(recipientCreationMetadata, 'company.priority', null) ||
    _get(recipientCreationMetadata, 'individual.priority', null)
  );
};

export const isProduction = () =>
  process.env.REACT_APP_ENVIRONMENT_NAME === 'PROD' ||
  process.env.REACT_APP_ENVIRONMENT_NAME === 'PROD_STAGING';

export const isEmulated = () =>
  process.env.REACT_APP_ENVIRONMENT_NAME === 'EMULATOR';

export const isLocal = () => process.env.NODE_ENV === 'development';

export const goToProductionLanding = () =>
  window.location.replace('https://www.hedgeflows.com/');

export const getSubstractedEmail = (str) =>
  str &&
  str.substr(0, 2) +
    '***@***' +
    str.substr(str.lastIndexOf('.'), str.length - str.lastIndexOf('.'));

export const getGoToStepQuery = (
  url: string,
  URLQuery: URLSearchParams,
  stepNumber: number
) => {
  URLQuery.set('step', stepNumber.toString());

  return `${url}?${URLQuery.toString()}`;
};

export const getAccountDetailsForCopy = (
  valuesMappings: string[][],
  title = '',
  lineBreaksForMail = false
) => {
  if (lineBreaksForMail) {
    return valuesMappings.reduce(
      (total, [key, value]) => total + `${key}: ${value}%0D%0A`,
      `${title}%0D%0A`
    );
  }

  return valuesMappings.reduce(
    (total, [key, value]) =>
      total +
      `${key}: ${value}
`,
    title
  );
};

export const getSwiftAccountDetails = (entityAccountDetails) =>
  entityAccountDetails.find(
    (item): item is IEntityPriorityAccountDetails =>
      item.type === IEntityAccountDetailsType.priority
  );
export const getRegularAccountDetailsEUR = (entityAccountDetails) =>
  entityAccountDetails.find(
    (item): item is IEntityRegularSwiftAccountDetails =>
      item.type === IEntityAccountDetailsType.regularSwift &&
      item.currencyCode === 'EUR'
  );
export const getRegularAccountDetailsGBP = (entityAccountDetails) =>
  entityAccountDetails.find(
    (item): item is IEntityRegularAccountDetails =>
      item.type === IEntityAccountDetailsType.regular &&
      item.currencyCode === 'GBP'
  );

export const copyTextToClipboard = (text: string) => {
  navigator.clipboard.writeText(text);
};

/**
 *By default the rate markup (on the mid rate) is 0.004 (0.4%).
 *The rate service only returns a pay rate (mid + 0.004).
 *This function reverses the default markup if the rateType = mid, and makes receive become mid - 0.004).
 *This function will not be required once the rates service can return pay, mid and receive rates.
 *Also the mid and receive rates are not 100% accurate for clients on non-default markups.
 */
export const getRateByRateType = (
  rate: UseCurrencyRateReturnValues['rate'],
  rateType: FOLLOWED_CURRENCY_RATE_TYPE,
  isInverted: boolean
) => {
  if (!rate) {
    return null;
  }

  let normalRate = rate;

  if (rateType === 'receive') {
    normalRate = rate * 1.008;
  }
  if (rateType === 'mid') {
    normalRate = rate * 1.004;
  }

  return isInverted ? 1 / normalRate : normalRate;
};

export const transformCurrencyToSelectOption = (currency: ICurrency) => ({
  value: currency,
  label: currency.code,
  icon: currency.countryCode,
});

type ResultDateFormat = 'DD MMM, YYYY';

export const formatToDate = (
  date: string | number | Date | dayjs.Dayjs | null | undefined,
  initialFormat: OptionType | undefined,
  resultFormat: ResultDateFormat | undefined
) => dayjs(date, initialFormat).format(resultFormat);

export const firstCharacterToUppercase = (string: string) =>
  string.charAt(0).toUpperCase() + string.slice(1);
